import { firestore } from '@psl/functions/firebase/firebaseAccess';
import { userPrivateProfileDoc } from '@psl/functions/firebase/refs/privateProfileDocRef';
import { CommentChatConnectionStatus } from '@psl/models/ClientModels';
import { useEffect } from 'react';
import { firstValueFrom } from 'rxjs';
import { centerModalOption } from '../../appComponents/ModalWrapper';
import { useIsMobileLayout } from '../../components/styles/responsive';
import { currentUserId, useCurrentUserId } from '../../functions/auth/utils';
import { docDataWithId } from '../../functions/firebase/rxfireExtensions';
import { useNavigateToStorefront } from '../../functions/hooks/useNavigateToStorefront';
import { CancelError } from '../../functions/hooks/useOnPromise';
import { usePersistFn } from '../../functions/hooks/usePersistFn';
import { useAppModalNavigation } from '../../functions/navigation/utils/routeHelpers';
import { wrapNavigatorScreen } from '../../functions/navigation/utils/wrapNavigatorScreen';
import { usePresentLogin } from '../user/functions/usePresentLogin';

export const CommentChatStartupPage = wrapNavigatorScreen(() => {
  const navigation = useAppModalNavigation();

  const presentLogin = usePresentLogin();
  const userId = useCurrentUserId();
  const navigateToStorefront = useNavigateToStorefront();
  const isMobile = useIsMobileLayout();

  const start = usePersistFn(() => {
    const createCommentChat = async () => {
      navigateToStorefront();

      const uid = currentUserId()!;
      const profile = await firstValueFrom(
        docDataWithId(userPrivateProfileDoc(uid)),
      );

      const navigateToCreateCommentChatFlow = () => {
        navigation.navigate('createCommentChat', {
          onDone: () => {
            navigation.pop(1);
          },
        });
      };

      if (
        profile.commentchat?.onboardingFinished &&
        profile.commentchat.connectionStatus ===
          CommentChatConnectionStatus.Success
      ) {
        if (profile.commentchat.waitingForAppSetup) {
          await firestore().collection('privateProfiles').doc(uid).update({
            'commentchat.waitingForAppSetup': false,
            'commentchat.appSetupFinished': true,
          });
        }
        navigateToCreateCommentChatFlow();
        return;
      }

      navigation.navigate('setupCommentChat', {
        onFinished: () => {
          setTimeout(() => {
            navigateToCreateCommentChatFlow();
          }, 1000);
        },
      });
    };

    if (isMobile) {
      void createCommentChat();
    } else {
      setTimeout(() => {
        void createCommentChat();
      }, 500);
    }
  });

  const initialize = usePersistFn(async () => {
    if (userId) {
      start();
      return;
    }

    presentLogin({
      skipOnboarding: true,
      title:
        'Please login your account and then continue to create commentchat',
    })
      .then(() => {
        setTimeout(start, 500);
      })
      .catch(e => {
        if (e instanceof CancelError) {
          navigation.goBack();
          return;
        }
        alert(`Sorry, an error occurred while login: ${e.message}`);
        console.error(e);
      });
  });

  useEffect(() => {
    navigation.goBack();

    setTimeout(() => {
      void initialize();
    }, 1000);
  }, [navigation, initialize]);

  return null;
}, centerModalOption);
